import React, { useEffect, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { Route, Switch, useLocation } from 'react-router-dom';
import { setContext } from 'src/api';
import { IS_DEVELOPMENT, IS_PRODUCTION } from 'src/config/env';
import { SITE_FALLBACK_ID } from 'src/config/sites';
import { resolveRoutePath, RouteRuntimeContext } from 'src/config/sites/SiteDefinition';
import { useAccount } from 'src/core/hooks/account';
import { useEnabledSiteModuleKeys, useSiteDefinition } from 'src/core/provider/SiteProvider';
import { isAdminContext } from 'src/core/utils';

const HomeRoutes: React.FC = () => {
    const dispatch = useDispatch();
    const { isLoggedIn, hasPrivilege } = useAccount();
    const site = useSiteDefinition();
    const enabledModules = useEnabledSiteModuleKeys();

    const location = useLocation();
    const isAdmin = isAdminContext(location.pathname);
    setContext(isAdmin ? 'admin' : 'customer', isLoggedIn);

    const runtimeContext = useMemo<RouteRuntimeContext>(
        () => ({
            isProduction: IS_PRODUCTION,
            isDevelopment: IS_DEVELOPMENT,
            isAdminContext: isAdmin,
            isLoggedIn,
            hasPrivilege,
        }),
        [hasPrivilege, isAdmin, isLoggedIn]
    );

    const activeRoutes = useMemo(() => {
        return site.routes.filter(
            route => enabledModules.has(route.key) && (!route.condition || route.condition(runtimeContext))
        );
    }, [enabledModules, runtimeContext, site]);

    useEffect(() => {
        if (site.id !== SITE_FALLBACK_ID && site.init) {
            site.init({ dispatch, runtime: runtimeContext, enabledModules });
        }
    }, [dispatch, enabledModules, runtimeContext, site]);

    return (
        <Switch>
            {activeRoutes.map(route => {
                const Component = route.component;
                return (
                    <Route
                        key={route.key}
                        path={resolveRoutePath(route.route)}
                        exact={route.exact}
                        render={props => <Component {...props} />}
                    />
                );
            })}
        </Switch>
    );
};

export default HomeRoutes;
